import React, { useState } from 'react';
import PropTypes from 'prop-types';
import appConfig from 'config/app.config';
import {
	getCurrentGameStepId, 
	getPrevGameStepId, 
	checkIfNextBtnIsDisplayed, 
	checkIfConditionalNextStepId
} from 'helpers/game-helper';
import {getText} from 'helpers/text-helper';
import {themesData} from 'data/themes-data';
import GameStepNav from 'components/ui/dev-tools/game-step-nav';
import GameStepVideo from 'components/game-steps/game-step-video/game-step-video';
import GameStepText from 'components/game-steps/game-step-text/game-step-text';
import GameStepQR from 'components/game-steps/game-step-qr/game-step-qr';
import GameStepQuestions from 'components/game-steps/game-step-questions/game-step-questions';
// import GameStepDilemmaResult from 'components/game-steps/game-step-dilemma-result/game-step-dilemma-result';
import Button from 'components/ui/button/button';
import './game.scss';

const Game = (props) => {
	/* Props */
	const {
		languageId,
		deviceInfo, 
		gameData, 
		groupData, 
		updateGroup, 
		handleLogout,
	} = props;

	/* Get theme data (default is first theme) */
	const themeData = (gameData.themeId 
		? themesData.find((t) => {return t.id === gameData.themeId;})
		: themesData[0]
	);

	/* Get current game step id */
	const [gameStepId, setGameStepId] = useState(getCurrentGameStepId(themeData, groupData));

	const [isLoading, setIsLoading] = useState(false);

	const prevGameStepId = getPrevGameStepId(gameStepId, themeData);
	

	/**
	 * Go to previous game step
	 */
	const goToPrevGameStep = () => {
		if (prevGameStepId) {
		/* Get current game step index */
			updateGroup({currentGameStepId: prevGameStepId});
			setGameStepId(prevGameStepId);
		}
	};

	/**
	 * Go to next game step
	 */
	const goToNextGameStep = () => {
		/* Get current game step index */
		const gameStepIndex = themeData.gameSteps.findIndex((step) => {return step.id === gameStepId;});

		// if (themeData.gameSteps[gameStepIndex].type === 'questions') {
		// 	/* Check if required number of options has been selected */
		// 	const allQuestionsAnswered = themeData.gameSteps[gameStepIndex].questions.every((question) => {
		// 		const groupQuestionData = (groupData.questions 
		// 			? groupData.questions.find((q) => {
		// 				return (q.questionId === question.id);
		// 			})
		// 			: null
		// 		);
		// 		return (groupQuestionData && groupQuestionData.optionId);
		// 	});		
		// 	if (!allQuestionsAnswered) return;		
		// }

		if (gameStepIndex + 1 < themeData.gameSteps.length) {
			/* Go to next game step */
			const nextGameStepId = checkIfConditionalNextStepId(gameStepData, groupData) 
				? themeData.gameSteps[gameStepIndex].nextStepId 
				: themeData.gameSteps[gameStepIndex + 1].id;

			setGameStepId(nextGameStepId);
			
			/* Update group progress */
			if (
				!groupData.currentGameStepId ||
				themeData.gameSteps.findIndex((step) => {
					return step.id === groupData.currentGameStepId;
				}) < (gameStepIndex + 1)
			) {
				updateGroup({currentGameStepId: nextGameStepId});
			}
		} else {
			/* Last game step, go to individual survey */
			if (appConfig.env === 'development') {
				window.location.href = 'http://' + appConfig.gameUrl + '/survey/' + gameData.id;
			} else {
				window.location.href = 'https://' + appConfig.gameUrl + '/survey/' + gameData.id;
			}
			
		}
	};

	/**
	 * Handles toggleing between loading and not loading
	 */
	const toggleLoading = (bool) => {
		setIsLoading(bool);
	};

	/* Get current game step index */
	const gameStepIndex = themeData.gameSteps.findIndex((step) => {return step.id === gameStepId;});

	/* Get current game step data */
	const gameStepData = themeData.gameSteps[gameStepIndex];

	let gameSubType = null;

	/* Get current game step component */
	let GameStepComponent = GameStepText;
	if (gameStepData.type === 'questions') {
		GameStepComponent = GameStepQuestions;
		if (
			gameStepData.questions && 
			gameStepData.questions.length > 0 
		) {
			gameSubType = gameStepData.questions[0].type;
		}
	}
	if (gameStepData.type === 'survey') GameStepComponent = GameStepQR;
	if (gameStepData.type === 'video') GameStepComponent = GameStepVideo;
	// if (gameStepData.type === 'dilemma-result') GameStepComponent = GameStepDilemmaResult;

	/* Check if next btn is displayed */
	const showNextStepBtn = checkIfNextBtnIsDisplayed(gameStepData, groupData);

	return (
		<div className={'Game ' + deviceInfo.orientation}>
			
			{/* Group */}
			<div className="Game-group" />

			{/* Logout btn */}
			<div className='Game-logoutBtn' onClick={() => {handleLogout();}} />

			{/* Game step page */}
			<div className={'Game-content ' + gameStepData.type + (gameSubType ? ' ' + gameSubType : '')}>
				<GameStepComponent
					toggleIsLoading={toggleLoading}
					languageId={languageId}
					prevGameStepId={prevGameStepId}
					deviceInfo={deviceInfo}
					gameData={gameData}
					gameStepData={gameStepData}
					groupData={groupData}
					goToNextGameStep={goToNextGameStep}
					goToPrevGameStep={goToPrevGameStep}
				/>
			</div>

			{/* Game footer */}
			<div className={'Game-footer ' + gameStepData.type}>

				{prevGameStepId && <div className="Game-prevBtn">
					<Button 
						classes={['orange', 'back', 'fullHeight']}
						isLoading={isLoading}
						isDisabled={isLoading}
						text={getText('general_back', languageId)}
						onClick={() => {goToPrevGameStep();}}
					/>
				</div>}
		
				{/* Button: next step */}
				{showNextStepBtn && <div className="Game-nextBtn">
					<Button 
						classes={['orange', 'next', 'fullHeight']}
						isLoading={isLoading}
						isDisabled={isLoading}
						text={getText(gameStepData.nextBtn, languageId)}
						onClick={() => {goToNextGameStep();}}
					/>
				</div>}
			</div>

			{/* Dev tools: game step navigation */}
			{appConfig.showDevTools && <GameStepNav 
				gameStepId={gameStepId} 
				gameSteps={themeData.gameSteps} 
				setGameStepId={setGameStepId} 
			/>}

			
		</div>
	);
};

Game.propTypes = {
	languageId: PropTypes.string.isRequired,
	deviceInfo: PropTypes.object.isRequired,
	gameData: PropTypes.object.isRequired,
	groupData: PropTypes.object.isRequired,
	updateGroup: PropTypes.func.isRequired,
	handleLogout: PropTypes.func.isRequired,
};

export default Game;